import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { COMMUNITY_JOIN_API } from "@/lib/apis";
import { cn } from "@/lib/utils";
import { IJoinCommunityQueryData, IJoinCommunityResponseDTO } from "@/types/community";
import { useMutation, UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

type TLeaveCommunityAlertDialogProps = {
	communityId: string;
	channelId?: string;
	displayText?: string;
	className?: string;
};

type TLeaveVariables = { type: "community" | "channel" };

function LeaveCommunityAlertDialog({
	communityId,
	channelId,
	displayText,
	className,
}: TLeaveCommunityAlertDialogProps) {
	const queryClient = useQueryClient();
	const isChannel = channelId !== undefined;

	const { mutate: leave, isLoading } = useMutation<
		IJoinCommunityResponseDTO,
		Error,
		TLeaveVariables,
		{ previousData: IJoinCommunityQueryData | undefined }
	>(
		async ({ type }: { type: "community" | "channel" }) => {
			const body = type === "channel" ? { channelId } : { communityId };

			const response = await fetch(COMMUNITY_JOIN_API, {
				method: "DELETE",
				body: JSON.stringify(body),
			});

			const data = await response.json();

			if (!response.ok) {
				throw new Error(data.message);
			}

			return data;
		},
		{
			onMutate: () => {
				queryClient.setQueryData<IJoinCommunityQueryData>(["join-status", communityId], {
					data: {
						joined: false,
						requested: false,
					},
				});
			},
			onSuccess: (data) => {
				toast.success(data.message);

				queryClient.invalidateQueries(["join-status", communityId]);
			},
			onError: (error, __, context) => {
				toast.error(error.message);

				if (context?.previousData) {
					queryClient.setQueryData<IJoinCommunityQueryData>(
						["join-status", communityId],
						context.previousData
					);
				}
			},
			onSettled: () => {
				queryClient.invalidateQueries({ queryKey: ["join-status", communityId] });
			},
		} as UseMutationOptions<
			IJoinCommunityResponseDTO,
			Error,
			TLeaveVariables,
			{ previousData: IJoinCommunityQueryData | undefined }
		>
	);

	return (
		<AlertDialog>
			<AlertDialogTrigger asChild>
				<button
					type="button"
					className={cn(
						className ?? "w-full md:w-44",
						"flex items-center justify-center gap-1 rounded-md bg-gray-400 px-4 py-2 font-medium text-white focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:opacity-50"
					)}
				>
					{displayText}
				</button>
			</AlertDialogTrigger>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>
						Are you sure you want to leave this {isChannel ? "channel" : "community"}?
					</AlertDialogTitle>

					<AlertDialogDescription>
						Leaving the community will remove you from the community and you will no longer be able
						to interact in the {isChannel ? "channel" : "community"}.
					</AlertDialogDescription>
				</AlertDialogHeader>

				<AlertDialogFooter>
					<AlertDialogCancel>Cancel</AlertDialogCancel>
					{isChannel ? (
						<>
							<AlertDialogAction disabled={isLoading} onClick={() => leave({ type: "community" })}>
								<span className="text-red-500">Leave Community</span>
							</AlertDialogAction>

							<AlertDialogAction disabled={isLoading} onClick={() => leave({ type: "channel" })}>
								<span className="text-red-500">Leave Channel</span>
							</AlertDialogAction>
						</>
					) : (
						<AlertDialogAction disabled={isLoading} onClick={() => leave({ type: "community" })}>
							<span className="text-red-500">Leave Community</span>
						</AlertDialogAction>
					)}
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}

export default LeaveCommunityAlertDialog;
